import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, FileText, CheckCircle, XCircle, Clock, Pencil, Trash2, ChevronDown, X, Zap, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import FacturaPDF from '../components/FacturaPDF';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';

export default function Facturas() {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [deletingFactura, setDeletingFactura] = useState(null);
  const queryClient = useQueryClient();

  // Estado para selector de cliente
  const [clienteSearch, setClienteSearch] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const { data: facturas, isLoading } = useQuery({
    queryKey: ['facturas'],
    queryFn: async () => {
      const response = await api.get('/facturas');
      return response.data;
    },
  });

  const { data: clientes } = useQuery({
    queryKey: ['clientes'],
    queryFn: async () => {
      const response = await api.get('/clientes');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/facturas', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success('Factura creada exitosamente');
      setShowModal(false);
      resetClienteSelector();
    },
    onError: () => toast.error('Error al crear factura'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.patch(`/facturas/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success('Factura actualizada exitosamente');
      setShowEditModal(false);
      setSelectedFactura(null);
    },
    onError: () => toast.error('Error al actualizar factura'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/facturas/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success('Factura eliminada exitosamente');
      setDeletingFactura(null);
    },
    onError: () => toast.error('Error al eliminar factura'),
  });

  const estadoMutation = useMutation({
    mutationFn: ({ id, estado }) => api.patch(`/facturas/${id}/estado/${estado}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success('Estado actualizado');
    },
    onError: () => toast.error('Error al actualizar estado'),
  });

  const generarMasivoMutation = useMutation({
    mutationFn: () => api.post('/facturas/generar-masivo'),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      const { generadas, errores, detalles } = response.data;
      if (generadas > 0) {
        toast.success(`Se generaron ${generadas} facturas exitosamente`);
      } else if (errores > 0) {
        toast.error(`Hubo ${errores} errores al generar facturas`);
      } else {
        toast('No se generaron facturas nuevas (posiblemente ya existen)');
      }
      console.log('Detalles de facturacion masiva:', detalles);
    },
    onError: () => toast.error('Error al ejecutar facturacion masiva'),
  });

  const emitirArcaMutation = useMutation({
    mutationFn: (facturaId) => api.post(`/arca/emitir/${facturaId}`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['facturas'] });
      toast.success(`Factura emitida en ARCA. CAE: ${response.data.cae}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error al emitir en ARCA');
    },
  });

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowClienteDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredFacturas = facturas?.filter((f) =>
    `${f.numeroFactura} ${f.cliente?.nombre} ${f.cliente?.apellido}`.toLowerCase().includes(search.toLowerCase())
  );

  const filteredClientes = clientes?.filter((c) =>
    `${c.nombre} ${c.apellido} ${c.dniCuit}`.toLowerCase().includes(clienteSearch.toLowerCase())
  );

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'pagada': return <CheckCircle size={16} className="text-green-600" />;
      case 'vencida': return <XCircle size={16} className="text-red-600" />;
      default: return <Clock size={16} className="text-yellow-600" />;
    }
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'pagada': return 'bg-green-100 text-green-800';
      case 'vencida': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const resetClienteSelector = () => {
    setClienteSearch('');
    setSelectedCliente(null);
    setShowClienteDropdown(false);
  };

  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(`${cliente.nombre} ${cliente.apellido}`);
    setShowClienteDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCliente) {
      toast.error('Selecciona un cliente');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const subtotal = Number(formData.get('subtotal'));
    const ivaPorcentaje = Number(formData.get('ivaPorcentaje')) || 21;
    const ivaMonto = subtotal * (ivaPorcentaje / 100);
    const total = subtotal + ivaMonto;

    const data = {
      numeroFactura: formData.get('numeroFactura'),
      clienteId: selectedCliente.id,
      fechaVencimiento: formData.get('fechaVencimiento'),
      subtotal,
      ivaPorcentaje,
      ivaMonto,
      total,
      observaciones: formData.get('observaciones'),
      items: [{
        concepto: formData.get('concepto'),
        cantidad: 1,
        precioUnitario: subtotal,
        subtotal,
      }],
    };
    createMutation.mutate(data);
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const subtotal = Number(formData.get('subtotal'));
    const ivaPorcentaje = Number(formData.get('ivaPorcentaje')) || 21;
    const ivaMonto = subtotal * (ivaPorcentaje / 100);
    const total = subtotal + ivaMonto;

    const data = {
      numeroFactura: formData.get('numeroFactura'),
      fechaVencimiento: formData.get('fechaVencimiento'),
      subtotal,
      ivaPorcentaje,
      ivaMonto,
      total,
      observaciones: formData.get('observaciones'),
    };
    updateMutation.mutate({ id: selectedFactura.id, data });
  };

  const openEditModal = (factura) => {
    setSelectedFactura(factura);
    setShowEditModal(true);
  };

  const openPDFModal = (factura) => {
    setSelectedFactura(factura);
    setShowPDFModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Facturas</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => generarMasivoMutation.mutate()}
            disabled={generarMasivoMutation.isPending}
            className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50">
            <Zap size={20} />
            <span>{generarMasivoMutation.isPending ? 'Generando...' : 'Facturacion Masiva'}</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus size={20} />
            <span>Nueva Factura</span>
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar factura..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Factura</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Emision</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredFacturas?.map((factura) => (
              <tr key={factura.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">{factura.numeroFactura}</td>
                <td className="px-6 py-4 text-gray-600">
                  {factura.cliente?.nombre} {factura.cliente?.apellido}
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {new Date(factura.fechaEmision).toLocaleDateString('es-AR')}
                </td>
                <td className="px-6 py-4 text-gray-600">
                  {new Date(factura.fechaVencimiento).toLocaleDateString('es-AR')}
                </td>
                <td className="px-6 py-4 font-medium text-gray-900">
                  ${Number(factura.total).toLocaleString('es-AR')}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getEstadoClass(factura.estado)}`}>
                    {getEstadoIcon(factura.estado)}
                    <span className="capitalize">{factura.estado}</span>
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    factura.esFantasia ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {factura.esFantasia ? 'Fantasia' : 'ARCA'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-1">
                    {factura.estado === 'pendiente' && (
                      <button
                        onClick={() => estadoMutation.mutate({ id: factura.id, estado: 'pagada' })}
                        className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                        title="Marcar como pagada">
                        Pagar
                      </button>
                    )}
                    {factura.esFantasia && (
                      <button
                        onClick={() => emitirArcaMutation.mutate(factura.id)}
                        disabled={emitirArcaMutation.isPending}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                        title="Emitir en ARCA">
                        <Building2 size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(factura)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Editar">
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeletingFactura(factura)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Eliminar">
                      <Trash2 size={16} />
                    </button>
                    <button
                      onClick={() => openPDFModal(factura)}
                      className="p-1.5 text-gray-600 hover:bg-gray-50 rounded"
                      title="Ver PDF">
                      <FileText size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Nueva Factura */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Nueva Factura</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numero de Factura</label>
                <input name="numeroFactura" required className="w-full px-3 py-2 border rounded-lg" placeholder="A-0001-00000001" />
              </div>

              {/* Selector de Cliente */}
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={clienteSearch}
                    onChange={(e) => {
                      setClienteSearch(e.target.value);
                      setShowClienteDropdown(true);
                      if (selectedCliente && e.target.value !== `${selectedCliente.nombre} ${selectedCliente.apellido}`) {
                        setSelectedCliente(null);
                      }
                    }}
                    onFocus={() => setShowClienteDropdown(true)}
                    placeholder="Buscar cliente por nombre o DNI..."
                    className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                  {selectedCliente ? (
                    <button
                      type="button"
                      onClick={resetClienteSelector}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  ) : (
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  )}
                </div>

                {showClienteDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredClientes?.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">No se encontraron clientes</div>
                    ) : (
                      filteredClientes?.map((cliente) => (
                        <button
                          key={cliente.id}
                          type="button"
                          onClick={() => handleSelectCliente(cliente)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0">
                          <div className="font-medium text-gray-900">
                            {cliente.nombre} {cliente.apellido}
                          </div>
                          <div className="text-sm text-gray-500">
                            DNI/CUIT: {cliente.dniCuit} | {cliente.localidad || 'Sin localidad'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Concepto</label>
                <input name="concepto" required className="w-full px-3 py-2 border rounded-lg" placeholder="Servicio de Internet - Mes..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal ($)</label>
                  <input name="subtotal" type="number" step="0.01" required className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IVA (%)</label>
                  <input name="ivaPorcentaje" type="number" defaultValue="21" className="w-full px-3 py-2 border rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Vencimiento</label>
                <input name="fechaVencimiento" type="date" required className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea name="observaciones" rows={2} className="w-full px-3 py-2 border rounded-lg"></textarea>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetClienteSelector(); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || !selectedCliente}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {createMutation.isPending ? 'Generando...' : 'Generar Factura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Factura */}
      {showEditModal && selectedFactura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Editar Factura</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numero de Factura</label>
                <input
                  name="numeroFactura"
                  defaultValue={selectedFactura.numeroFactura}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                <input
                  value={`${selectedFactura.cliente?.nombre} ${selectedFactura.cliente?.apellido}`}
                  disabled
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal ($)</label>
                  <input
                    name="subtotal"
                    type="number"
                    step="0.01"
                    defaultValue={selectedFactura.subtotal}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IVA (%)</label>
                  <input
                    name="ivaPorcentaje"
                    type="number"
                    defaultValue={selectedFactura.ivaPorcentaje || 21}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Vencimiento</label>
                <input
                  name="fechaVencimiento"
                  type="date"
                  defaultValue={selectedFactura.fechaVencimiento?.split('T')[0]}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea
                  name="observaciones"
                  rows={2}
                  defaultValue={selectedFactura.observaciones || ''}
                  className="w-full px-3 py-2 border rounded-lg"
                ></textarea>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedFactura(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminacion */}
      <ConfirmDeleteModal
        open={!!deletingFactura}
        title="Eliminar Factura"
        message={
          deletingFactura ? (
            <>¿Estás seguro de eliminar la factura <strong>{deletingFactura.numeroFactura}</strong>?</>
          ) : ''
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(deletingFactura.id)}
        onCancel={() => setDeletingFactura(null)}
      />

      {/* Modal PDF */}
      {showPDFModal && selectedFactura && (
        <FacturaPDF
          factura={selectedFactura}
          onClose={() => { setShowPDFModal(false); setSelectedFactura(null); }}
        />
      )}
    </div>
  );
}
